import { QUESTION_TYPES, ANALYSIS_REQUIREMENTS } from './constants';

/**
 * Base prompt structure for all question types
 */
const BASE_PROMPT = {
  system: `You are an expert data analyst specializing in [QUESTION_TYPE] analysis. 
Analyze the provided responses and generate insights following these guidelines:
1. Be concise and clear
2. Focus on actionable insights
3. Use specific numbers and percentages when available
4. Highlight key themes and patterns
5. Provide practical recommendations
6. Structure insights for optimal UI display
7. Include relevant metrics for each section
8. Consider engagement levels and response patterns
9. Highlight significant trends and outliers
10. Provide context-aware recommendations`,
  
  format: `Structure your response as follows:
{
  "executiveSummary": {
    "content": "Brief overview of key findings",
    "metrics": {
      "totalResponses": number,
      "engagementRate": number,
      "keyMetrics": {
        // Type-specific metrics
      }
    }
  },
  "analysis": {
    "content": ["Analysis point 1", "Analysis point 2", ...],
    "metrics": {
      // Type-specific metrics
    },
    "themes": ["Theme 1", "Theme 2", ...],
    "patterns": ["Pattern 1", "Pattern 2", ...]
  },
  "insights": {
    "content": ["Insight 1", "Insight 2", ...],
    "metrics": {
      // Type-specific metrics
    },
    "confidence": "high|medium|low"
  },
  "recommendations": {
    "content": ["Recommendation 1", "Recommendation 2", ...],
    "priority": "high|medium|low",
    "impact": "high|medium|low",
    "feasibility": "high|medium|low"
  },
  "visualization": {
    "preferredCharts": ["chartType1", "chartType2", ...],
    "dataFormat": "formatType",
    "keyMetrics": ["metric1", "metric2", ...]
  }
}`,
  
  context: `Consider the following context:
1. Event/Session Type: [SESSION_TYPE]
2. Question Context: [QUESTION_CONTEXT]
3. Response Volume: [RESPONSE_COUNT]
4. Engagement Level: [ENGAGEMENT_LEVEL]
5. Time Period: [TIME_PERIOD]`
};

/**
 * Question type specific prompts
 */
export const QUESTION_PROMPTS = {
  [QUESTION_TYPES.OPEN_ENDED]: {
    ...BASE_PROMPT,
    user: `Analyze these open-ended responses:
1. Identify main themes and patterns
2. Extract key insights and recommendations
3. Consider engagement levels (likes) for importance
4. Focus on noteworthy responses
5. Provide actionable next steps
6. Structure insights for:
   - Theme clustering visualization
   - Sentiment analysis display
   - Key quote highlighting
7. Include specific metrics:
   - Theme frequency and distribution
   - Sentiment scores and distribution
   - Engagement metrics per theme
   - Response length analysis
8. Highlight:
   - Most impactful quotes
   - Common pain points
   - Emerging trends
   - Actionable suggestions
9. Consider context:
   - Response volume and distribution
   - Time-based patterns
   - Engagement patterns
10. Format for optimal UI display:
    - Clear hierarchy of themes
    - Easy-to-scan insights
    - Visual-friendly metrics
    - Interactive elements support`,
    
    visualization: {
      preferredCharts: [
        'wordCloud',
        'themeCluster',
        'sentimentBar',
        'engagementHeatmap',
        'quoteHighlights'
      ],
      dataFormat: 'theme-based',
      keyMetrics: [
        'themeFrequency',
        'sentimentScore',
        'engagementRate',
        'responseLength',
        'timeDistribution'
      ]
    }
  },

  [QUESTION_TYPES.BINARY]: {
    ...BASE_PROMPT,
    user: `Analyze these binary (Yes/No) responses:
1. Calculate response distribution
2. Identify patterns in responses
3. Consider engagement levels
4. Provide actionable insights
5. Structure insights for:
   - Binary distribution visualization
   - Response pattern analysis
   - Engagement correlation
6. Include specific metrics:
   - Yes/No distribution percentages
   - Engagement rates by response type
   - Response time patterns
   - Demographic correlations
7. Highlight:
   - Key decision factors
   - Response patterns
   - Engagement trends
   - Actionable recommendations
8. Consider context:
   - Response volume and distribution
   - Time-based patterns
   - Engagement patterns
9. Format for optimal UI display:
   - Clear binary distribution
   - Pattern visualization
   - Correlation highlights
   - Interactive elements support`,
    
    visualization: {
      preferredCharts: [
        'pieChart',
        'barChart',
        'timeSeries',
        'correlationMatrix'
      ],
      dataFormat: 'binary',
      keyMetrics: [
        'yesNoRatio',
        'engagementRate',
        'responseTime',
        'demographicCorrelation'
      ]
    }
  },

  [QUESTION_TYPES.MULTIPLE_CHOICE]: {
    ...BASE_PROMPT,
    user: `Analyze these multiple choice responses:
1. Calculate distribution for each option
2. Identify patterns and correlations
3. Consider engagement levels
4. Provide actionable insights
5. Structure insights for:
   - Option distribution visualization
   - Pattern analysis
   - Correlation mapping
6. Include specific metrics:
   - Option selection percentages
   - Option engagement rates
   - Cross-option correlations
   - Demographic patterns
7. Highlight:
   - Most/least popular options
   - Option relationships
   - Selection patterns
   - Actionable recommendations
8. Consider context:
   - Response volume per option
   - Time-based patterns
   - Engagement patterns
9. Format for optimal UI display:
   - Clear option distribution
   - Pattern visualization
   - Correlation mapping
   - Interactive elements support`,
    
    visualization: {
      preferredCharts: [
        'barChart',
        'pieChart',
        'correlationMatrix',
        'heatmap'
      ],
      dataFormat: 'categorical',
      keyMetrics: [
        'optionDistribution',
        'engagementRate',
        'correlationScore',
        'patternStrength'
      ]
    }
  },

  [QUESTION_TYPES.MULTIPLE_RATING]: {
    ...BASE_PROMPT,
    user: `Analyze these multiple rating responses:
1. Calculate average ratings for each criterion
2. Analyze rating distributions
3. Identify patterns and correlations
4. Provide actionable insights`
  },

  [QUESTION_TYPES.SCALE]: {
    ...BASE_PROMPT,
    user: `Analyze these scale responses:
1. Calculate average scores
2. Analyze score distribution
3. Identify patterns
4. Provide actionable insights
5. Structure insights for:
   - Score distribution visualization
   - Trend analysis
   - Pattern identification
6. Include specific metrics:
   - Average and median scores
   - Score distribution
   - Trend analysis
   - Pattern strength
7. Highlight:
   - Score trends
   - Distribution patterns
   - Outliers
   - Actionable recommendations
8. Consider context:
   - Score distribution
   - Time-based patterns
   - Engagement patterns
9. Format for optimal UI display:
   - Clear score distribution
   - Trend visualization
   - Pattern highlights
   - Interactive elements support`,
    
    visualization: {
      preferredCharts: [
        'histogram',
        'lineChart',
        'boxPlot',
        'scatterPlot'
      ],
      dataFormat: 'numeric',
      keyMetrics: [
        'averageScore',
        'scoreDistribution',
        'trendStrength',
        'patternScore'
      ]
    }
  },

  [QUESTION_TYPES.STACK_RANK]: {
    ...BASE_PROMPT,
    user: `Analyze these stack rank responses:
1. Calculate priority distribution
2. Identify ranking patterns
3. Consider engagement levels
4. Provide actionable insights
5. Structure insights for:
   - Ranking distribution visualization
   - Priority analysis
   - Pattern identification
6. Include specific metrics:
   - Priority distribution
   - Ranking consistency
   - Pattern strength
   - Engagement correlation
7. Highlight:
   - Top priorities
   - Ranking patterns
   - Consistency levels
   - Actionable recommendations
8. Consider context:
   - Ranking distribution
   - Time-based patterns
   - Engagement patterns
9. Format for optimal UI display:
   - Clear ranking distribution
   - Priority visualization
   - Pattern highlights
   - Interactive elements support`,
    
    visualization: {
      preferredCharts: [
        'barChart',
        'heatmap',
        'networkGraph',
        'priorityMatrix'
      ],
      dataFormat: 'ranked',
      keyMetrics: [
        'priorityScore',
        'rankingConsistency',
        'patternStrength',
        'engagementRate'
      ]
    }
  },

  [QUESTION_TYPES.MATRIX]: {
    ...BASE_PROMPT,
    user: `Analyze these matrix responses:
1. Calculate scores for each criterion
2. Analyze distribution patterns
3. Identify correlations
4. Provide actionable insights
5. Structure insights for:
   - Matrix visualization
   - Correlation analysis
   - Pattern identification
6. Include specific metrics:
   - Criterion scores
   - Correlation strength
   - Pattern consistency
   - Engagement impact
7. Highlight:
   - Key correlations
   - Pattern strength
   - Score distribution
   - Actionable recommendations
8. Consider context:
   - Score distribution
   - Correlation patterns
   - Engagement patterns
9. Format for optimal UI display:
   - Clear matrix visualization
   - Correlation mapping
   - Pattern highlights
   - Interactive elements support`,
    
    visualization: {
      preferredCharts: [
        'heatmap',
        'correlationMatrix',
        'scatterPlot',
        'networkGraph'
      ],
      dataFormat: 'matrix',
      keyMetrics: [
        'criterionScore',
        'correlationStrength',
        'patternConsistency',
        'engagementImpact'
      ]
    }
  },

  [QUESTION_TYPES.RATING]: {
    ...BASE_PROMPT,
    user: `Analyze these rating responses:
1. Calculate average rating
2. Analyze rating distribution
3. Identify patterns
4. Provide actionable insights`
  },

  [QUESTION_TYPES.RANKING]: {
    ...BASE_PROMPT,
    user: `Analyze these ranking responses:
1. Calculate ranking distribution
2. Identify ranking patterns
3. Consider engagement levels
4. Provide actionable insights`
  }
};

/**
 * Response validation and cleaning
 */
export const validateResponse = (response, questionType) => {
  if (!response) return null;

  // Ensure required sections exist
  const requirements = ANALYSIS_REQUIREMENTS[questionType];
  if (!requirements) return null;

  // Clean and structure response
  const cleanedResponse = {
    executiveSummary: {
      content: response.executiveSummary?.content || '',
      metrics: {
        totalResponses: response.executiveSummary?.metrics?.totalResponses || 0,
        engagementRate: response.executiveSummary?.metrics?.engagementRate || 0,
        keyMetrics: response.executiveSummary?.metrics?.keyMetrics || {}
      }
    },
    analysis: {
      content: Array.isArray(response.analysis?.content) ? response.analysis.content : [],
      metrics: response.analysis?.metrics || {},
      themes: Array.isArray(response.analysis?.themes) ? response.analysis.themes : [],
      patterns: Array.isArray(response.analysis?.patterns) ? response.analysis.patterns : []
    },
    insights: {
      content: Array.isArray(response.insights?.content) ? response.insights.content : [],
      metrics: response.insights?.metrics || {},
      confidence: response.insights?.confidence || 'medium'
    },
    recommendations: {
      content: Array.isArray(response.recommendations?.content) ? response.recommendations.content : [],
      priority: response.recommendations?.priority || 'medium',
      impact: response.recommendations?.impact || 'medium',
      feasibility: response.recommendations?.feasibility || 'medium'
    },
    visualization: {
      preferredCharts: Array.isArray(response.visualization?.preferredCharts) 
        ? response.visualization.preferredCharts 
        : [],
      dataFormat: response.visualization?.dataFormat || 'default',
      keyMetrics: Array.isArray(response.visualization?.keyMetrics) 
        ? response.visualization.keyMetrics 
        : []
    },
    _meta: {
      parsing: 'success',
      validation: 'success',
      foundSections: Object.keys(response).filter(key => 
        typeof response[key] === 'object' && response[key] !== null
      ),
      missingSections: [],
      questionType,
      timestamp: new Date().toISOString()
    }
  };

  // Validate required sections based on question type
  const requiredSections = requirements.requiredThemes || [];
  const missingSections = requiredSections.filter(section => 
    !cleanedResponse[section] || 
    (Array.isArray(cleanedResponse[section]) && cleanedResponse[section].length === 0)
  );

  if (missingSections.length > 0) {
    cleanedResponse._meta.validation = 'partial';
    cleanedResponse._meta.missingSections = missingSections;
  }

  return cleanedResponse;
}; 